<script setup>
// Resultados da lupa da lista, em blocos como o WhatsApp:
// - Conversas: o contato bate pelo nome/telefone/e-mail ou pelo nº da conversa;
// - Contatos: clientes achados que não estão no bloco Conversas. Clicar abre a
//   conversa mais recente dele, ou a "Nova conversa" já com o contato;
// - Mensagens: um cartão por MENSAGEM com o trecho destacado. Clicar abre a
//   conversa já rolando até ela (o ConversationView lê ?messageId=).
import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import ContactAPI from 'dashboard/api/contacts';
import {
  snippetAround,
  contactsOutsideConversations,
} from './conversationListSearchHelpers';

const props = defineProps({
  query: { type: String, default: '' },
  results: { type: Array, default: () => [] },
  conversations: { type: Array, default: () => [] },
  contacts: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  isLoadingPeople: { type: Boolean, default: false },
  hasMore: { type: Boolean, default: false },
});
const emit = defineEmits(['loadMore']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { highlightContent } = useMessageFormatter();

const escapeHtml = text => {
  const p = document.createElement('p');
  p.appendChild(document.createTextNode(text || ''));
  return p.innerHTML;
};

const snippetHtml = message => {
  const raw =
    message.content ||
    message.content_attributes?.email?.subject ||
    (message.attachments || []).find(a => a.file_type === 'audio')
      ?.transcribed_text ||
    '';
  return highlightContent(
    escapeHtml(snippetAround(raw, props.query)),
    props.query,
    'searchkey--highlight'
  );
};

const contactName = message =>
  message.conversation?.contact_name ||
  (message.message_type === 0 ? message.sender?.name : '') ||
  t('CHAT_LIST.MESSAGE_SEARCH.UNKNOWN_CONTACT');

const inboxName = message => message.conversation?.inbox_name || '';
const when = message => dynamicTime(message.created_at);
const isOutgoing = message => message.message_type === 1;

const goToConversation = (displayId, query = {}) =>
  router.push({
    path: frontendURL(
      conversationUrl({ accountId: route.params.accountId, id: displayId }),
      query
    ),
  });

const open = message =>
  goToConversation(message.conversation_id, { messageId: message.id });

const onlyContacts = computed(() =>
  contactsOutsideConversations(props.contacts, props.conversations)
);

const conversationName = conversation =>
  conversation.contact?.name ||
  conversation.contact?.phone_number ||
  t('CHAT_LIST.MESSAGE_SEARCH.UNKNOWN_CONTACT');
const conversationDetail = conversation =>
  [conversation.contact?.phone_number, conversation.inbox?.name]
    .filter(Boolean)
    .join(' · ');
const contactDetail = contact =>
  contact.phone_number || contact.email || contact.identifier || '';

const composeContactId = ref(null);
const composeTrigger = ref(null);
const openingContactId = ref(null);

const openContact = async contact => {
  if (openingContactId.value) return;
  openingContactId.value = contact.id;
  try {
    const { data } = await ContactAPI.getConversations(contact.id);
    const latest = (data?.payload || [])[0];
    if (latest) {
      goToConversation(latest.id);
      return;
    }
    await store.dispatch('contacts/show', { id: contact.id });
    composeContactId.value = String(contact.id);
    await nextTick();
    composeTrigger.value?.click();
  } finally {
    openingContactId.value = null;
  }
};
const closeCompose = () => {
  composeContactId.value = null;
};

const showEmpty = computed(
  () =>
    !props.isLoading &&
    !props.isLoadingPeople &&
    props.results.length === 0 &&
    props.conversations.length === 0 &&
    onlyContacts.value.length === 0
);
</script>

<template>
  <div class="flex-1 overflow-y-auto">
    <div
      v-if="showEmpty"
      class="px-4 py-10 text-sm text-center text-n-slate-11"
    >
      {{ t('CHAT_LIST.MESSAGE_SEARCH.NO_RESULTS', { query }) }}
    </div>

    <template v-if="conversations.length">
      <h4
        class="px-4 pt-3 pb-1 m-0 text-xs font-semibold uppercase text-n-brand"
      >
        {{ t('CHAT_LIST.MESSAGE_SEARCH.SECTION_CONVERSATIONS') }}
      </h4>
      <ul class="m-0 p-0 list-none" data-testid="search-conversations">
        <li
          v-for="conversation in conversations"
          :key="`c-${conversation.id}`"
          class="border-b border-n-weak"
        >
          <button
            type="button"
            class="flex items-center w-full gap-3 px-4 py-2.5 text-left transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:bg-n-alpha-2"
            @click="goToConversation(conversation.id)"
          >
            <Avatar
              :name="conversationName(conversation)"
              :size="32"
              rounded-full
            />
            <span class="flex flex-col flex-1 min-w-0">
              <span class="flex items-center justify-between gap-2">
                <span class="text-sm font-medium truncate text-n-slate-12">
                  {{ conversationName(conversation) }}
                </span>
                <span class="text-xs whitespace-nowrap text-n-slate-10">
                  {{ `#${conversation.id}` }}
                </span>
              </span>
              <span class="text-xs truncate text-n-slate-10">
                {{ conversationDetail(conversation) }}
              </span>
            </span>
          </button>
        </li>
      </ul>
    </template>

    <template v-if="onlyContacts.length">
      <h4
        class="px-4 pt-3 pb-1 m-0 text-xs font-semibold uppercase text-n-brand"
      >
        {{ t('CHAT_LIST.MESSAGE_SEARCH.SECTION_CONTACTS') }}
      </h4>
      <ul class="m-0 p-0 list-none" data-testid="search-contacts">
        <li
          v-for="contact in onlyContacts"
          :key="`p-${contact.id}`"
          class="border-b border-n-weak"
        >
          <button
            type="button"
            class="flex items-center w-full gap-3 px-4 py-2.5 text-left transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:bg-n-alpha-2"
            :disabled="openingContactId === contact.id"
            @click="openContact(contact)"
          >
            <Avatar
              :name="contact.name || contactDetail(contact)"
              :size="32"
              rounded-full
            />
            <span class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ contact.name || contactDetail(contact) }}
              </span>
              <span class="text-xs truncate text-n-slate-10">
                {{ contactDetail(contact) }}
              </span>
            </span>
            <Spinner
              v-if="openingContactId === contact.id"
              class="size-4 text-n-brand"
            />
          </button>
        </li>
      </ul>
    </template>

    <div v-if="isLoadingPeople" class="flex justify-center my-3">
      <Spinner class="text-n-brand" />
    </div>

    <template v-if="results.length">
      <h4
        class="px-4 pt-3 pb-1 m-0 text-xs font-semibold uppercase text-n-brand"
      >
        {{ t('CHAT_LIST.MESSAGE_SEARCH.SECTION_MESSAGES') }}
      </h4>
      <p class="px-4 pb-1 m-0 text-xs text-n-slate-10">
        {{ t('CHAT_LIST.MESSAGE_SEARCH.SCOPE_NOTE') }}
      </p>
    </template>
    <ul v-if="results.length" class="m-0 p-0 list-none">
      <li
        v-for="message in results"
        :key="message.id"
        class="border-b border-n-weak"
      >
        <button
          type="button"
          class="flex flex-col w-full gap-1 px-4 py-3 text-left transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:bg-n-alpha-2"
          @click="open(message)"
        >
          <div class="flex items-center justify-between w-full gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ contactName(message) }}
            </span>
            <span class="text-xs whitespace-nowrap text-n-slate-10">
              {{ when(message) }}
            </span>
          </div>
          <div
            class="flex items-center gap-1.5 w-full min-w-0 text-xs text-n-slate-11"
          >
            <span
              class="flex-shrink-0 size-3.5 text-n-slate-10"
              :class="
                isOutgoing(message)
                  ? 'i-lucide-corner-up-left'
                  : 'i-lucide-corner-down-right'
              "
            />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span
              class="line-clamp-2 break-words"
              v-html="snippetHtml(message)"
            />
          </div>
          <span v-if="inboxName(message)" class="text-[11px] text-n-slate-9">
            {{ inboxName(message) }}
          </span>
        </button>
      </li>
    </ul>
    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>
    <div v-else-if="hasMore && results.length" class="flex justify-center py-3">
      <NextButton
        faded
        slate
        sm
        :label="t('CHAT_LIST.MESSAGE_SEARCH.LOAD_MORE')"
        @click="emit('loadMore')"
      />
    </div>

    <ComposeConversation
      v-if="composeContactId"
      :contact-id="composeContactId"
      is-modal
      @close="closeCompose"
    >
      <template #trigger="{ toggle }">
        <span ref="composeTrigger" class="hidden" @click="toggle" />
      </template>
    </ComposeConversation>
  </div>
</template>
